import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import _ from 'lodash';
import { Field, useFormState } from 'react-final-form';
import {
  DateField,
  FormLegend,
  InputField,
  SimpleSelectField,
  MultiSelectField,
} from '@unite-us/app-components';
import today from 'common/utils/today';
import { BINARY_GENDERS, canEditGender } from 'src/common/utils/gender';
import minBirthDate from 'common/utils/minBirthDate';
import { required, validateSSN } from 'src/common/form/utils/rffValidations';

// Sub-component for Gender Field
function GenderField({ genderDisabled, modifiedGenders }) {
  if (genderDisabled) {
    return (
      <SimpleSelectField
        className="basic-information-gender"
        id="gender"
        label="Gender"
        value="undisclosed"
        options={[{ display_name: 'Undisclosed', value: 'undisclosed' }]}
        optionSettings={{ labelKey: 'display_name', valueKey: 'value' }}
        disabled
        searchable
        menuPortalTarget={document.body}
      />
    );
  }

  return (
    <Field name="gender" validate={required}>
      {({ input, meta }) => (
        <SimpleSelectField
          className="basic-information-gender"
          id="gender"
          label="Gender"
          value={input.value}
          onChange={input.onChange}
          onBlur={input.onBlur}
          options={modifiedGenders}
          optionSettings={{ labelKey: 'display_name', valueKey: 'value' }}
          error={meta.touched ? meta.error : undefined}
          required
          searchable
          menuPortalTarget={document.body}
        />
      )}
    </Field>
  );
}

GenderField.propTypes = {
  genderDisabled: PropTypes.bool.isRequired,
  modifiedGenders: PropTypes.array.isRequired,
};

// Sub-component for Sexuality Fields
function SexualityFields({ peopleEnums, shouldShowSexualityFields, isSexualOrientationOtherSelected }) {
  if (!shouldShowSexualityFields) return null;

  return (
    <>
      <Field name="sexuality">
        {({ input, meta }) => (
          <MultiSelectField
            className="basic-information-sexuality"
            id="sexuality"
            label="Sexual Orientation"
            value={input.value || []}
            onChange={input.onChange}
            onBlur={input.onBlur}
            options={peopleEnums.sexual_orientations}
            getOptionLabel={(option) => option.display_name}
            getOptionValue={(option) => option.value}
            clearable
            error={meta.touched ? meta.error : undefined}
            menuPortalTarget={document.body}
          />
        )}
      </Field>
      {isSexualOrientationOtherSelected && (
        <div className="row">
          <div className="col-xs-12 col-sm-6">
            <Field name="sexuality_other">
              {(params) => (
                <InputField
                  {...params}
                  maxLength={198}
                  label="Other (Please Specify)"
                  id="sexuality-other"
                  autoComplete="off"
                />
              )}
            </Field>
          </div>
        </div>
      )}
    </>
  );
}

SexualityFields.propTypes = {
  peopleEnums: PropTypes.object.isRequired,
  shouldShowSexualityFields: PropTypes.bool.isRequired,
  isSexualOrientationOtherSelected: PropTypes.bool.isRequired,
};

// Sub-component for demographic select fields (Marital Status, Race, Ethnicity)
function DemographicField({
  name, fieldId, label, options,
}) {
  return (
    <Field name={name} validate={required}>
      {({ input, meta }) => (
        <SimpleSelectField
          className={`basic-information-${fieldId}`}
          id={fieldId}
          label={label}
          value={input.value}
          onChange={input.onChange}
          onBlur={input.onBlur}
          options={options}
          optionSettings={{ labelKey: 'display_name', valueKey: 'value' }}
          error={meta.touched ? meta.error : undefined}
          required
          searchable
          menuPortalTarget={document.body}
        />
      )}
    </Field>
  );
}

DemographicField.propTypes = {
  name: PropTypes.string.isRequired,
  fieldId: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
};

function BasicInformation(
  {
    enums,
    limitedGenderOptions,
    shouldShowSexualityFields,
    isExistingContact,
  },
) {
  // Use React Final Form's useFormState hook to get form values
  const { values } = useFormState({ subscription: { values: true } });

  const peopleEnums = _.wget(enums, 'people', {});
  const genderDisabled = isExistingContact &&
    !canEditGender({ gender: values.gender, flag: limitedGenderOptions });
  const modifiedGenders = useMemo(() => {
    if (limitedGenderOptions) {
      return peopleEnums.genders.filter(
        (genderOption) => BINARY_GENDERS.includes(genderOption.value.toLowerCase()),
      );
    }

    return peopleEnums.genders;
  }, [peopleEnums, limitedGenderOptions, BINARY_GENDERS]);

  const sexualOrientationsSelected = _.get(values, 'sexuality', []);
  const isSexualOrientationOtherSelected = _.includes(sexualOrientationsSelected, 'other');

  return (
    <div className="basic-information">
      <h2 className="basic-information__title">Personal Information</h2>
      <FormLegend />

      <hr className="mt-1 mb-4" />

      <div className="row">
        <div className="col-xs-12 col-sm-6">
          <Field name="first_name" validate={required}>
            {(params) => (
              <InputField
                {...params}
                label="First Name"
                id="first-name"
                autoComplete="given-name"
                required
              />
            )}
          </Field>
        </div>

        <div className="col-xs-12 col-sm-6">
          <Field name="last_name" validate={required}>
            {(params) => (
              <InputField
                {...params}
                label="Last Name"
                id="last-name"
                autoComplete="family-name"
                required
              />
            )}
          </Field>
        </div>
      </div>

      <div className="row">
        <div className="col-xs-12 col-sm-6">
          <Field name="date_of_birth" validate={required}>
            {(params) => (
              <DateField
                {...params}
                label="Date of Birth"
                id="date-of-birth"
                minDate={minBirthDate()}
                maxDate={today().toString()}
                required
              />
            )}
          </Field>
        </div>
      </div>

      <div className="row">
        <div className="col-xs-12 col-sm-6">
          <Field name="ssn" validate={validateSSN}>
            {(params) => (
              <InputField
                {...params}
                id="social-security-number"
                className="basic-information__social-security-number"
                label="Social Security Number"
                placeholder={`*** - ** - ${values.ssn_last4 || '***'}`}
                mask="999-99-9999"
                autoComplete="off"
              />
            )}
          </Field>
        </div>
      </div>
      <div className="row">
        <div className="col-xs-12">
          <GenderField genderDisabled={genderDisabled} modifiedGenders={modifiedGenders} />
          <SexualityFields
            peopleEnums={peopleEnums}
            shouldShowSexualityFields={shouldShowSexualityFields}
            isSexualOrientationOtherSelected={isSexualOrientationOtherSelected}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-xs-12">
          <DemographicField
            name="marital_status"
            fieldId="marital-status"
            label="Marital Status"
            options={peopleEnums.marital_status}
          />
          <DemographicField
            name="race"
            fieldId="race"
            label="Race"
            options={peopleEnums.race}
          />
          <DemographicField
            name="ethnicity"
            fieldId="ethnicity"
            label="Ethnicity"
            options={peopleEnums.ethnicity}
          />
        </div>
      </div>
    </div>
  );
}

BasicInformation.propTypes = {
  enums: PropTypes.object.isRequired,
  limitedGenderOptions: PropTypes.bool.isRequired,
  shouldShowSexualityFields: PropTypes.bool.isRequired,
  isExistingContact: PropTypes.bool.isRequired,
};

BasicInformation.fields = [
  'first_name',
  'last_name',
  'date_of_birth',
  'gender',
  'sexuality',
  'sexuality_other',
  'marital_status',
  'race',
  'ethnicity',
  'ssn',
  'ssn_last4',
];

export default BasicInformation;
